import { apiOssProxyInternal } from "./api";
import {
  listBucketObjectsResponseSchema,
  type ListBucketObjectsResponse,
} from "./schemas/s3-bucket-objects";
import {
  objectOverviewResponseSchema,
  type ObjectOverviewResponse,
} from "./schemas/s3-object-overview";
import { listBucketsResponseSchema, type S3BucketRow } from "./schemas/s3-bucket";
import {
  downloadObjectResponseSchema,
  type DownloadObjectResponse,
} from "./schemas/s3-object-download";

const AWS_S3_BUCKET_OSS_PATH = "/aws/s3/bucket";

export async function listBuckets(): Promise<S3BucketRow[]> {
  const resp = await apiOssProxyInternal.get(AWS_S3_BUCKET_OSS_PATH);
  if (!resp.data) throw new Error("Buckets response not returned");
  return listBucketsResponseSchema.parse(resp.data).buckets;
}

const LIST_OBJECTS_MAX_KEYS = "250";

export async function listBucketObjects(
  bucketName: string,
  params?: { prefix?: string; continuationToken?: string }
): Promise<ListBucketObjectsResponse> {
  const segment = encodeURIComponent(bucketName);
  const query = new URLSearchParams();
  query.set("maxKeys", LIST_OBJECTS_MAX_KEYS);
  if (params?.prefix !== undefined && params.prefix !== "") {
    query.set("prefix", params.prefix);
  }
  if (params?.continuationToken !== undefined && params.continuationToken !== "") {
    query.set("continuationToken", params.continuationToken);
  }
  const search = `?${query.toString()}`;
  const resp = await apiOssProxyInternal.get(
    `${AWS_S3_BUCKET_OSS_PATH}/${segment}/object${search}`
  );
  if (!resp.data) throw new Error("Bucket objects response not returned");
  return listBucketObjectsResponseSchema.parse(resp.data);
}

/**
 * GET /ops/oss-proxy/internal/aws/s3/bucket/:bucket/object?overview=true&key=...
 * → OSS /internal/aws/s3/bucket/:bucket/object?overview=true&key=...
 */
export async function getObjectOverview(
  bucketName: string,
  objectKey: string
): Promise<ObjectOverviewResponse> {
  const segment = encodeURIComponent(bucketName);
  const query = new URLSearchParams();
  query.set("overview", "true");
  query.set("key", objectKey);
  const resp = await apiOssProxyInternal.get(
    `${AWS_S3_BUCKET_OSS_PATH}/${segment}/object?${query.toString()}`
  );
  if (!resp.data) throw new Error("Object overview response not returned");
  return objectOverviewResponseSchema.parse(resp.data);
}

/**
 * GET /ops/oss-proxy/internal/aws/s3/bucket/:bucket/object?download=true&key=...&region=...
 * → OSS JSON: `{ downloadUrl, expiresAt, expiresInSeconds }`.
 */
export async function fetchBucketObjectDownloadInfo(
  bucketName: string,
  objectKey: string,
  params?: { region?: string }
): Promise<DownloadObjectResponse> {
  const segment = encodeURIComponent(bucketName);
  const query = new URLSearchParams();
  query.set("download", "true");
  query.set("key", objectKey);
  if (params?.region) query.set("region", params.region);
  const resp = await apiOssProxyInternal.get(
    `${AWS_S3_BUCKET_OSS_PATH}/${segment}/object?${query.toString()}`
  );
  if (!resp.data) throw new Error("Object download response not returned");
  return downloadObjectResponseSchema.parse(resp.data);
}

/**
 * Loads the presigned URL in a hidden iframe so the main ops-dash tab is not navigated away
 * (avoids a blank tab / blank main window when assigning a cross-origin download URL).
 */
function startPresignedDownloadInHiddenFrame(url: string): void {
  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;left:-9999px;width:1px;height:1px;border:0;visibility:hidden";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 300_000);
}

export async function downloadBucketObject(
  bucketName: string,
  objectKey: string,
  params?: { region?: string }
): Promise<void> {
  const payload = await fetchBucketObjectDownloadInfo(bucketName, objectKey, params);
  startPresignedDownloadInHiddenFrame(payload.downloadUrl);
}
